<template>
  <b-modal
    id="case-feedback-modal"
    v-model="showModal"
    :title="$t('caseFeedback')"
    centered
    size="lg"
    hide-footer
    @hidden="resetForm"
  >
    <div class="feedback-modal">
      <div class="feedback-section mb-4">
        <h5 class="mb-3">{{ $t("wasAiCorrect") }}</h5>
        <div class="d-flex gap-3">
          <b-button
            :variant="aiCorrect === true ? 'success' : 'outline-success'"
            @click="aiCorrect = true"
            class="px-4"
          >
            {{ $t("yes") }}
          </b-button>
          <b-button
            :variant="aiCorrect === false ? 'danger' : 'outline-danger'"
            @click="aiCorrect = false"
            class="px-4"
          >
            {{ $t("no") }}
          </b-button>
        </div>
      </div>

      <div v-if="aiCorrect === false" class="feedback-section mb-4">
        <h5 class="mb-3">{{ $t("whichToothPlacedOrCutted") }}</h5>
        <div class="row">
          <div class="col-12 col-lg-5 mb-3 mb-lg-0">
            <div class="tooth-scheme-container">
              <TeethSelectorInput
                :modelValue="selectedTeeth"
                :palette="palette"
                @update:modelValue="selectedTeeth = $event"
              />
            </div>
          </div>
          <div class="col-12 col-lg-7">
            <h5 class="mb-3">{{ $t("whatWasTheProblem") }}</h5>
            <div class="problem-options">
              <div class="problem-grid">
                <div 
                  v-for="problem in problemOptions" 
                  :key="problem.value"
                  class="problem-box"
                  :class="{ 'selected': selectedProblems.includes(problem.value) }"
                  @click="toggleProblem(problem.value)"
                >
                  {{ $t(problem.label) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-4">
        <div v-if="aiCorrect === false && (selectedProblems.length === 0 || selectedTeeth.length === 0)" class="text-muted small">
          <span v-if="selectedProblems.length === 0">{{ $t("pleaseSelectAtLeastOneProblem") }}</span>
          <span v-else-if="selectedTeeth.length === 0">{{ $t("pleaseSelectAtLeastOneTooth") }}</span>
        </div>
        <div v-else></div>
        <div class="d-flex gap-2">
          <b-button variant="secondary" @click="closeModal">
            {{ $t("cancel") }}
          </b-button>
          <b-button 
            variant="primary" 
            @click="submitFeedback"
            :disabled="!canSubmit"
          >
            {{ $t("submit") }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import TeethSelectorInput from "@/components/TeethSelectorInput.vue";

export default {
  name: "CaseFeedbackModal",
  components: {
    TeethSelectorInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:show", "submit"],
  data() {
    return {
      aiCorrect: null,
      selectedTeeth: [],
      selectedProblems: [],
      palette: [
        { text: "cutted", color: "#6f42c1", reconstructionType: "cutted" },
        { text: "pontic", color: "#dc3545", reconstructionType: "pontic" },
      ],
      problemOptions: [
        { value: "occlusion", label: "feedbackProblemOcclusion" },
        { value: "buccal_lingual", label: "feedbackProblemBuccalLingual" },
        { value: "artefacts", label: "feedbackProblemArtefacts" },
        { value: "dies_scanmarkers", label: "feedbackProblemDiesScanmarkers" },
        { value: "wrong_tooth", label: "feedbackProblemWrongTooth" },
        { value: "position_template", label: "feedbackProblemPositionTemplate" },
        { value: "missing_tooth", label: "feedbackProblemMissingTooth" },
        { value: "wrong_interlock", label: "feedbackProblemWrongInterlock" },
        { value: "too_many_teeth", label: "feedbackProblemTooManyTeeth" },
        { value: "not_enough_teeth", label: "feedbackProblemNotEnoughTeeth" },
        { value: "too_big_teeth", label: "feedbackProblemTooBigTeeth" },
        { value: "too_small_teeth", label: "feedbackProblemTooSmallTeeth" },
        { value: "merged_teeth", label: "feedbackProblemMergedTeeth" },
        { value: "changed_healthy_teeth", label: "feedbackProblemChangedHealthyTeeth" },
        { value: "unusable_tooth", label: "feedbackProblemUnusableTooth" },
        { value: "wrong_shape", label: "feedbackProblemWrongShape" },
      ],
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    canSubmit() {
      if (this.aiCorrect === null) return false;
      if (this.aiCorrect === true) return true;
      if (this.aiCorrect === false) {
        return this.selectedProblems.length > 0 && this.selectedTeeth.length > 0;
      }
      return false;
    },
  },
  methods: {
    updateSelectedTeeth(teeth) {
      this.selectedTeeth = teeth;
    },
    toggleProblem(problemValue) {
      const index = this.selectedProblems.indexOf(problemValue);
      if (index > -1) {
        this.selectedProblems.splice(index, 1);
      } else {
        this.selectedProblems.push(problemValue);
      }
    },
    closeModal() {
      this.showModal = false;
    },
    submitFeedback() {
      const feedback = {
        aiCorrect: this.aiCorrect,
        selectedTeeth: this.selectedTeeth,
        selectedProblems: this.selectedProblems,
      };
      this.$emit("submit", feedback);
      this.closeModal();
    },
    resetForm() {
      this.aiCorrect = null;
      this.selectedTeeth = [];
      this.selectedProblems = [];
    },
  },
};
</script>

<style scoped>
.feedback-modal {
  padding: 1rem;
}

.feedback-section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.feedback-section:last-of-type {
  border-bottom: none;
}

.tooth-scheme-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.problem-options {
  padding: 0.5rem;
}

.problem-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.problem-box {
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 0.5rem;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  line-height: 1.3;
  text-align: center;
  user-select: none;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.problem-box:hover {
  border-color: #007bff;
  background-color: #f8f9ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.problem-box.selected {
  border-color: #007bff;
  background-color: #007bff;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.problem-box.selected:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}

@media (max-width: 991.98px) {
  .tooth-scheme-container {
    min-height: 250px;
  }
  
  .problem-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    max-height: 350px;
  }
}

@media (max-width: 767.98px) {
  .problem-grid {
    grid-template-columns: 1fr;
    max-height: 300px;
  }
  
  .problem-box {
    min-height: 50px;
    font-size: 0.8rem;
  }
}
</style>
<style lang="scss">
body.dark-layout {
  .feedback-modal {
    .feedback-section {
      border-bottom-color: rgba(255, 255, 255, 0.1);

      h5 {
        color: #fff;
      }
    }

    .tooth-scheme-container #toothLabels text {
      fill: #fff;
    }

    .problem-box {
      background-color: #091420;
      border-color: rgba(255, 255, 255, 0.2);
      color: #fff;

      &:hover {
        background-color: #02224c;
        border-color: #05fffa;
      }
    }
  }
}
</style>

